import styled, { createGlobalStyle, css } from 'styled-components';

// ===========================================================================
// Comprovante de movimento de estoque — estilos do IMPRESSO.
// Handoff de design "Comprovantes de impressão dos movimentos" (2026-09-13).
//
// Este arquivo é deliberadamente auto-contido e NÃO usa o kit de tela: o
// documento tem régua própria (piso de 9,5px, cinza nunca mais claro que
// #666, zero sombra, zero ícone decorativo) e vive em milímetros, não em rem.
// Puxa do tema só o que é identidade de marca (o #373737 das faixas escuras).
// A marca em si é o PNG da logomarca no cabeçalho — desde 2026-09-17, no lugar
// do quadrado azul com a inicial.
//
// ⚠️ UM PAPEL SÓ — A4 retrato (decisão do Caio, 2026-09-13, simplificando o
// contrato original de dois formatos). O que varia é a ÁREA que o documento
// ocupa nessa folha:
//
//   até 5 itens  → MEIA FOLHA + linha de recorte no meio: o operador corta e
//                  guarda a metade limpa para a próxima impressão;
//   6+ itens     → FOLHA INTEIRA, com o campo de observações da conferência.
//
// E a ESCALA segue a ÁREA, não o papel: meia folha usa a régua compacta (a
// mesma que o design aprovou para o A5 — e milímetro é milímetro, o tipo sai
// fisicamente do mesmo tamanho), folha inteira usa a régua generosa (+15%).
// Sem isso a régua generosa não cabe nos 135,5mm da meia folha.
// ===========================================================================

// Único papel do documento.
export const PAGE = {
   label: 'A4 retrato',
   size: '210mm 297mm',
   margin: '13mm',
   widthMm: 210,
   marginMm: 13,
};

// Altura útil da meia folha: metade do A4 (148,5mm) menos a margem superior
// do papel (13mm). É onde a linha de recorte cai — exatamente no meio da
// FOLHA, que é o que a tesoura enxerga.
export const HALF_AREA_MM = 135.5;

// Regra de área: até 8 itens cabem na meia folha (ajuste do Caio,
// 2026-09-13 — subiu de 5 depois que o rodapé perdeu a identidade da empresa
// e parou de quebrar em duas linhas).
//
// ⚠️ O teto é MEDIDA, não gosto. Orçamento da meia folha = 135,5mm ≈ 512px;
// com 8 itens o conteúdo fecha em ~494px na régua compacta (cabeçalho 35 +
// faixa 31 + dados 85 + tabela 226 + assinaturas 58 + rodapé 27 + gaps),
// sobrando ~18px. É apertado de propósito: uma faixa de identidade que quebre
// em duas linhas (PDV + documento longos) come quase toda a sobra.
//
// Se estourar, a falha é GRACIOSA: a `HalfArea` usa `min-height`, então ela
// cresce e a linha de recorte desce alguns milímetros do meio exato — o
// documento continua imprimindo em uma folha, só o corte fica menos centrado.
// Se a régua, a margem ou os blocos mudarem, RECALCULAR este número.
export const HALF_SHEET_MAX_ITEMS = 8;

// `@page` é regra de DOCUMENTO, não de elemento: não existe forma de aplicá-la
// só a um pedaço da página. É por isso que o comprovante mora em rota própria
// e monta esta folha de estilo global — que desmonta junto com a rota, sem
// deixar tamanho de papel grudado no resto do app.
export const PrintPageStyle = createGlobalStyle`
   @page {
      size: ${PAGE.size};
      margin: ${PAGE.margin};
   }
`;

// ---------------------------------------------------------------------------
// Barra de ferramentas da pré-visualização (só tela)
// ---------------------------------------------------------------------------
export const PreviewBar = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   flex-wrap: wrap;
   margin-bottom: 16px;

   .meta {
      min-width: 0;
   }

   h1 {
      margin: 0;
      font-size: 20px;
      line-height: 1.2;
      color: ${({ theme }) => theme.colors.textOnAppBackground};
   }

   /* A legenda diz POR QUE o documento saiu nessa área — transparência pedida
      pelo handoff, para o operador não achar que o sistema decidiu sozinho e
      sem critério (e para ele saber que pode cortar a folha). */
   p {
      margin: 4px 0 0;
      font-size: 12.5px;
      color: ${({ theme }) => theme.colors.textMutedOnAppBackground};
   }

   .acts {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
      flex-wrap: wrap;
   }
`;

// ---------------------------------------------------------------------------
// A folha
// ---------------------------------------------------------------------------

// Em tela: folha branca centralizada com a largura real do papel, para a
// pré-visualização não mentir sobre a quebra. Na impressão: sem sombra, sem
// borda, sem largura fixa — o `@page` manda.
export const Sheet = styled.div`
   width: ${PAGE.widthMm}mm;
   max-width: 100%;
   margin: 0 auto;
   padding: ${PAGE.marginMm}mm;
   background: #fff;
   color: #111;
   border-radius: 10px;
   box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
   font-family: 'Inter', 'Noto Sans', system-ui, sans-serif;
   font-variant-numeric: tabular-nums;

   /* A marca d'água de movimento excluído se posiciona por aqui. */
   position: relative;

   /* Redundante com a regra global de impressão de propósito: a folha carrega
      a própria garantia de cor, então o documento imprime certo mesmo se um
      dia for renderizado fora do container marcado com data-print-root. */
   -webkit-print-color-adjust: exact;
   print-color-adjust: exact;

   @media print {
      width: auto;
      max-width: none;
      margin: 0;
      padding: 0;
      border-radius: 0;
      box-shadow: none;
   }
`;

// Marca d'água de movimento EXCLUÍDO. Não estava no handoff (o design não
// sabia que imprimiríamos movimento excluído — decisão do Caio, 2026-09-12):
// comprovante anulado circulando no papel sem marca inequívoca é risco
// operacional. Fica atrás do conteúdo e não intercepta clique.
export const VoidWatermark = styled.div`
   position: absolute;
   inset: 0;
   display: flex;
   align-items: ${({ $full }) => ($full ? 'center' : 'flex-start')};
   justify-content: center;
   padding-top: ${({ $full }) => ($full ? 0 : 40)}mm;
   pointer-events: none;
   z-index: 0;

   span {
      transform: rotate(-24deg);
      font-size: ${({ $full }) => ($full ? 92 : 64)}px;
      font-weight: 800;
      letter-spacing: 0.08em;
      color: rgba(171, 32, 32, 0.13);
      border: 4px solid rgba(171, 32, 32, 0.13);
      border-radius: 14px;
      padding: 6px 22px;
      white-space: nowrap;
   }
`;

// Conteúdo acima da marca d'água.
export const SheetBody = styled.div`
   position: relative;
   z-index: 1;
   display: flex;
   flex-direction: column;
   gap: ${({ $full }) => ($full ? 12 : 8)}px;
`;

// Área do documento na MEIA FOLHA: altura fixa para a linha de recorte cair
// no meio exato da folha, e não onde o conteúdo terminar. É o que garante que
// a metade de baixo saia limpa e reaproveitável.
export const HalfArea = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   min-height: ${HALF_AREA_MM}mm;
`;

// Linha de recorte — só na meia folha. Zero ícone decorativo (régua do
// handoff): o tracejado é `border`, não caractere repetido, então não quebra
// em larguras diferentes nem desalinha na impressão.
export const CutLine = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   /* Sobe metade da própria altura para que o CENTRO da linha coincida com o
      meio da folha, já que a HalfArea acima tem a altura exata. */
   margin: -6px 0 0;
   color: #666;

   .rule {
      flex: 1;
      border-top: 1px dashed rgba(0, 0, 0, 0.45);
   }

   .label {
      font-size: 9.5px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      white-space: nowrap;
   }
`;

// ---------------------------------------------------------------------------
// 1. Cabeçalho
// ---------------------------------------------------------------------------
export const DocHeader = styled.header`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 14px;
   padding-bottom: ${({ $full }) => ($full ? 10 : 7)}px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.12);

   .brand {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
   }

   /* A marca é a logomarca em PNG (337×77), que já traz o próprio fundo
      escuro — nada de caixa/cor em volta. Altura fixa, largura pelo aspecto:
      ~123px na régua generosa, ~105px na compacta. Imagem sempre imprime
      (a supressão de fundo do navegador só pega background-color). */
   .mark {
      display: block;
      flex-shrink: 0;
      width: auto;
      height: ${({ $full }) => ($full ? 28 : 24)}px;
      object-fit: contain;
   }

   .legal {
      min-width: 0;
   }

   .legal .name {
      font-size: ${({ $full }) => ($full ? 12.5 : 11)}px;
      font-weight: 800;
      color: #111;
      line-height: 1.25;
   }

   .legal .ids {
      margin-top: 1px;
      font-size: ${({ $full }) => ($full ? 10.5 : 9.5)}px;
      color: #666;
      line-height: 1.3;
   }

   .titles {
      text-align: right;
      flex-shrink: 0;
   }

   .eyebrow {
      font-size: ${({ $full }) => ($full ? 10 : 9.5)}px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #666;
   }

   .title {
      margin-top: 1px;
      font-size: ${({ $full }) => ($full ? 20 : 17)}px;
      font-weight: 800;
      letter-spacing: -0.01em;
      color: #111;
      line-height: 1.15;
   }
`;

// ---------------------------------------------------------------------------
// 2. Faixa de identidade
// ---------------------------------------------------------------------------
export const IdentityBand = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: ${({ $full }) => ($full ? '9px 12px' : '7px 10px')};
   border-radius: 6px;
   background: ${({ theme }) => theme.colors.appBackground};
   color: #fff;

   .left {
      font-size: ${({ $full }) => ($full ? 16 : 14)}px;
      font-weight: 800;
      line-height: 1.2;
      min-width: 0;
   }

   .right {
      font-size: ${({ $full }) => ($full ? 12 : 10.5)}px;
      font-weight: 700;
      text-align: right;
      flex-shrink: 0;
      /* Branco puro na direita brigaria com o título; 0.82 mantém hierarquia
         e continua legível no papel (o piso de #666 é para fundo claro). */
      color: rgba(255, 255, 255, 0.82);
   }
`;

// ---------------------------------------------------------------------------
// 3. Dados do movimento
// ---------------------------------------------------------------------------
export const DataBox = styled.div`
   border: 1px solid rgba(0, 0, 0, 0.12);
   border-radius: ${({ $full }) => ($full ? 8 : 6)}px;

   .grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: ${({ $full }) => ($full ? '10px 16px' : '6px 12px')};
      padding: ${({ $full }) => ($full ? '12px 14px' : '8px 10px')};
   }

   .cell {
      min-width: 0;
   }

   .cap {
      font-size: 9.5px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #666;
      line-height: 1.3;
   }

   .val {
      margin-top: 1px;
      font-size: ${({ $full }) => ($full ? 12.5 : 11)}px;
      font-weight: 700;
      color: #111;
      line-height: 1.3;
      overflow-wrap: anywhere;
   }

   .desc {
      padding: ${({ $full }) => ($full ? '10px 14px' : '7px 10px')};
      border-top: 1px solid rgba(0, 0, 0, 0.1);
   }

   .desc .val {
      font-weight: 400;
      font-size: ${({ $full }) => ($full ? 12 : 10.5)}px;
   }
`;

// Pílula de situação do impresso — versão sem cor de fundo saturada (o papel
// come cor) e sem depender do StatusBadge de tela, que tem outra escala.
export const DocStatus = styled.span`
   display: inline-flex;
   align-items: center;
   padding: 1px 7px;
   border-radius: 999px;
   font-size: 9.5px;
   font-weight: 800;
   letter-spacing: 0.04em;
   border: 1px solid
      ${({ $variant }) =>
         $variant === 'void' ? 'rgba(171,32,32,0.45)' : 'rgba(0,0,0,0.2)'};
   color: ${({ $variant }) => ($variant === 'void' ? '#8a1f1f' : '#222')};
   background: ${({ $variant }) =>
      $variant === 'void' ? 'rgba(171,32,32,0.08)' : 'transparent'};
   white-space: nowrap;
`;

// ---------------------------------------------------------------------------
// 4. Tabela de itens
// ---------------------------------------------------------------------------

// ⚠️ SEM `border-radius` + `overflow: hidden` aqui, de propósito — e o motivo
// é de impressão, não de estética: recortar o contêiner de uma tabela que
// PAGINA pode cortar as linhas que caem na página seguinte. É a armadilha do
// FC7 §H.4.1 num contexto novo (lá o recorte aprisionava um dropdown; aqui
// engoliria a segunda página). Canto reto na tabela é o preço, e é barato.
export const ItemsTable = styled.table`
   width: 100%;
   border-collapse: collapse;
   border: 1px solid rgba(0, 0, 0, 0.12);

   thead th {
      background: ${({ theme }) => theme.colors.appBackground};
      color: #fff;
      font-size: ${({ $full }) => ($full ? 10 : 9.5)}px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      text-align: left;
      padding: ${({ $full }) => ($full ? '7px 10px' : '5px 8px')};
      white-space: nowrap;
   }

   tbody td {
      font-size: ${({ $full }) => ($full ? 12 : 10.5)}px;
      color: #222;
      padding: ${({ $full }) => ($full ? '6px 10px' : '4px 8px')};
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      line-height: 1.3;
   }

   /* Zebra do handoff: #fff / #fcfcfd. Sutil de propósito — zebra forte
      imprime como faixa cinza e pesa a folha. */
   tbody tr:nth-child(even) td {
      background: #fcfcfd;
   }

   tfoot td {
      font-size: ${({ $full }) => ($full ? 13 : 12)}px;
      font-weight: 800;
      color: #111;
      padding: ${({ $full }) => ($full ? '7px 10px' : '5px 8px')};
      border-top: 1px solid rgba(0, 0, 0, 0.2);
      background: #fff;
   }

   .num {
      text-align: right;
      font-variant-numeric: tabular-nums;
   }

   .center {
      text-align: center;
   }

   /* Código do produto no azul escuro da marca, como o handoff pede. */
   .code {
      font-weight: 700;
      color: #0b3a63;
      white-space: nowrap;
   }

   .idx {
      width: 22px;
      color: #666;
      text-align: right;
   }
`;

// ---------------------------------------------------------------------------
// 5. Observações (só folha inteira) · assinaturas · autenticação
// ---------------------------------------------------------------------------

// Espaço livre para a conferência escrever à mão. **Sem linhas pautadas**
// (ajuste do Caio, 2026-09-13) e com metade da altura que as três linhas
// ocupavam — a caixa com borda já delimita a área, o pautado era ruído.
// Na meia folha este bloco não existe: não cabe, e a operação escreve sobre
// as linhas de assinatura.
export const NotesBox = styled.div`
   border: 1px solid rgba(0, 0, 0, 0.12);
   border-radius: 8px;
   padding: 10px 14px;

   .cap {
      font-size: 10px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #666;
   }

   .space {
      height: 42px;
   }
`;

const signatureRow = css`
   display: grid;
   gap: ${({ $full }) => ($full ? 20 : 14)}px;
   align-items: end;
`;

export const SignatureArea = styled.div`
   ${signatureRow}
   /* Meia folha: assinaturas e autenticação na MESMA linha (o handoff é
      explícito para o formato compacto). Folha inteira: assinaturas em linha
      própria, autenticação em faixa embaixo. */
   grid-template-columns: ${({ $full }) => ($full ? '1fr 1fr' : '1fr 1fr auto')};
   margin-top: ${({ $full }) => ($full ? 8 : 6)}px;

   /* Na meia folha as assinaturas são o último bloco antes do recorte, então
      ficam ancoradas no fim da área — sem isso elas subiriam para junto da
      tabela e deixariam um vão estranho acima da linha de corte. */
   ${({ $full }) => (!$full ? 'margin-top: auto;' : '')}
`;

export const SignatureLine = styled.div`
   .rule {
      border-top: 1px solid rgba(0, 0, 0, 0.35);
      /* Espaço de caneta: o operador assina ACIMA da linha. */
      margin-top: ${({ $full }) => ($full ? 34 : 26)}px;
   }

   .role {
      margin-top: 3px;
      font-size: ${({ $full }) => ($full ? 10.5 : 9.5)}px;
      font-weight: 700;
      color: #222;
      line-height: 1.25;
   }

   .hint {
      margin-top: 1px;
      font-size: 9.5px;
      color: #666;
   }
`;

// Slot de autenticação RESERVADO E VAZIO (decisão do Caio, 2026-09-13): o QR,
// o código de verificação e o código de barras entram numa fase própria, que
// depende de hash persistido + rota pública (`/v/{id}`). O espaço fica
// guardado no grid para a fase 2 cair aqui sem re-layout — e vazio de
// propósito: código de verificação que não confere com nada é auditabilidade
// aparente, pior que ausência.
export const AuthSlot = styled.div`
   flex-shrink: 0;
   width: ${({ $full }) => ($full ? '100%' : '96px')};
   min-height: ${({ $full }) => ($full ? '0' : '58px')};
`;

// ---------------------------------------------------------------------------
// 6. Rodapé
// ---------------------------------------------------------------------------
export const DocFooter = styled.footer`
   margin-top: ${({ $full }) => ($full ? 10 : 7)}px;
   padding-top: ${({ $full }) => ($full ? 8 : 6)}px;
   border-top: 1px solid rgba(0, 0, 0, 0.12);
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 12px;

   .emit {
      font-size: 9.5px;
      color: #5c5c5c;
      line-height: 1.35;
      min-width: 0;
   }

   .notice {
      font-size: 9.5px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #5c5c5c;
      white-space: nowrap;
      flex-shrink: 0;
   }
`;
